<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { ColorLegend, SVGMap, SVGPolygonLayer } from "@urbaninstitute/dataviz-components/maps";
  import { Tooltip, LoadingWrapper, Block } from "@urbaninstitute/dataviz-components";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import * as topojson from "topojson-client";
  import { StateData, getStateName } from "$utils/states.js";
  import { geoAlbersUsaPr } from "$utils/projection.js";
  import { Tilemap } from "@urbaninstitute/dataviz-components/maps";
  import { getConfig } from "$lib/utils/config.js";
  import { getScale } from "$lib/utils/colors.js";
  import { formatTextTemplate } from "$lib/utils/text.js";

  /** @typedef {import("$utils/states").FeatureOptions} FeatureOptions */

  /**
   * @typedef {Object} Tooltip
   * @property {number} x
   * @property {number} y
   * @property {Record<string, any>} props
   */

  /**
   * @typedef {Object} Props
   * @property {Object[]} data
   * @property {string} metric
   * @property {string} currentState
   * @property {(props?: {map_id: string, fips?: string} | null) => void} onStateSelect
   * @property {import('$lib/types').mapType} [mapType]
   * @property {import("$lib/utils/colors").ScaleType} scaleType
   * @property {import("$lib/utils/colors").ColorPalette} [colorPalette="blue"]
   * @property {number[] | undefined} [scaleBreaks]
   * @property {[number, number] | undefined} [minMax]
   * @property {FeatureOptions} [featureOption="states"]
   * @property {string} [naColor="#cecece"]
   */

  /** @type {Props} */
  let {
    data,
    metric,
    currentState = $bindable(),
    onStateSelect,
    mapType,
    scaleType,
    colorPalette = "blue",
    scaleBreaks = undefined,
    minMax = undefined,
    featureOption = "states",
    naColor = "#cecece"
  } = $props();

  const config = getConfig()();

  const stateData = new StateData();

  /**
   * @param {string} rawName
   * @returns {string}
   */
  function cleanVariableName(rawName) {
    return rawName.toLowerCase().replace("share of housing within a half mile of", "");
  }

  const tooltipTextTemplate = "In <strong>[state]</strong>, <strong>[value]</strong> of housing is within a half mile of <strong>[variable_name]</strong>.";

  /** @type { string } */
  /** @type { string   | undefined } */
  /** @type { Tooltip   | undefined } */
  let tooltip = $state();
  const scaleValues = $derived(data.map((d) => /** @type {Record<string, any>} */ (d)[metric]));
  const currentColorScale = $derived(
    scaleBreaks
      ? getScale(scaleType, colorPalette, scaleValues, scaleBreaks)
      : getScale(scaleType, colorPalette, scaleValues, undefined, minMax)
  );

  // const geoData = topojson.feature(topoData, "states");
  // svelte-ignore state_referenced_locally
  let geoData = stateData.getFeatures(featureOption);

  /**
   * @param {any} geo
   * @param {any[]} d
   */
  async function joinData(geo, d) {
    const geoData = await geo;
    return geoData.map((/** @type {any} */ place) => {
      let result = {
        type: place.type,
        geometry: place.geometry,
        properties: place.properties
      };
      const match = d.find((/** @type {any} */ row) => row.map_id === place.properties.fips);
      if (match) {
        result.properties = Object.assign({}, result.properties, match);
      } else {
        // if there is no join data, fill the expected columns with N/A
        /** @type {Record<string, any>} */
        let missingProps = {};
        Array.from(config.columns.keys()).forEach((/** @type {any} */ col) => {
          if (!result.properties[col]) {
            missingProps[col] = "N/A";
          }
        });
        result.properties = Object.assign({}, result.properties, missingProps);
      }
      return result;
    });
  }

  /** @param {any} e @param {any} props */
  function handleStateClick(e, props) {
    onStateSelect(props);
    // logClickToGA(e.target, "tilemap-state-click--" + props.name);
  }

  /** @param {any} e @param {any} props */
  function handleStateHover(e, props) {
    tooltip = getTooltipProps(e.pageX, e.pageY, props);
  }

  /** @param {any} [e] */
  function onClear(e) {
    currentState = "";
    onStateSelect();
    // dispatch("stateSelect", currentState);
  }

  /**
   * @param {number} x
   * @param {number} y
   * @param {Record<string, any>} props
   * @returns {Tooltip}
   */
  function getTooltipProps(x, y, props) {
    // add in state name if not present
    if (props.map_id && !props.state) {
      props.state = getStateName(props.map_id);
    }
    return {
      x,
      y,
      props
    };
  }

  /**
   * Raise a dom node to top of siblings
   * @param { HTMLElement } el The DOM element to raise
   * @returns void
   */
  export function raise(el) {
    if (el.parentNode) {
      el.parentNode.appendChild(el);
    }
  }

  const joinedData = $derived(joinData(geoData, data));

  /** @param {any} feature @param {string} metric @returns {string} */
  function fillFunc(feature, metric) {
    return /** @type {string} */ (currentColorScale(feature[metric]));
  }

  /** @type {any} */
  const tileShape = $derived(mapType === "hex" ? mapType : "rect");
  /** @type {any} */
  const tileFill = $derived(
    /** @type {(d: any) => string} */ ((/** @type {any} */ d) => fillFunc(d, metric))
  );
  /** @type {any} */
  const geoFill = $derived(
    /** @type {(d: any) => string} */ ((/** @type {any} */ d) => fillFunc(d.properties, metric))
  );
</script>

<Block>
  <div style="max-width: 300px; margin: 1em auto 0 0;">
    <!-- <div class="legend-labels"><span>Smaller gap</span><span>Larger gap</span></div> -->
    <ColorLegend
      scale={currentColorScale}
      height={16}
      tickLineColor={"white"}
      tickLineWidth={2}
      tickFormat={config.columns.get(metric)?.legendFormat}
      margin={{ top: 0, right: 5, bottom: 5, left: 0 }}
    />
  </div>
</Block>
<div class="tile-map">
  {#if mapType == "geo"}
    {#await joinedData}
      <LoadingWrapper>
        <div class="map-placeholder"></div>
      </LoadingWrapper>
    {:then joinedMapData}
      <SVGMap
        features={joinedMapData}
        onbgclick={onClear}
        aspectRatio={4 / 2.5}
        projection={geoAlbersUsaPr}
      >
        <SVGPolygonLayer
          fill={geoFill}
          strokeWidth={config.map.strokeWidth}
          highlightFeature={{ fips: currentState }}
          hoverStroke={config.map.hoverStroke}
          hoverStrokeWidth={config.map.hoverStrokeWidth}
          onclick={(e) => handleStateClick(e.detail.e, e.detail.props)}
          onmousemove={(e) => handleStateHover(e.detail.e, e.detail.props)}
          onmouseout={() => (tooltip = undefined)}
        />
      </SVGMap>
    {/await}
  {:else}
    <Tilemap
      {data}
      shape={tileShape}
      {featureOption}
      fill={tileFill}
      strokeWidth={config.map.strokeWidth}
      highlightFeature={currentState}
      hoverStroke={config.map.hoverStroke}
      hoverStrokeWidth={config.map.hoverStrokeWidth}
      labelColor={config.map.fontColor}
      ariaTitle={"Map of US states"}
      onBgclick={onClear}
      onClick={handleStateClick}
      onMousemove={handleStateHover}
      onMouseout={() => (tooltip = undefined)}
    />
  {/if}
</div>
<div class="clear-selection-wrapper">
  <button
    onclick={(e) => {
      // logClickToGA(e.target, "clear-selection-click");
      onClear();
    }}
    style:visibility={currentState ? "visible" : "hidden"}>Clear state selection</button
  >
</div>
{#if tooltip}
  <Tooltip x={tooltip.x} y={tooltip.y} size="large" tooltipOverride={undefined}>
    <p>
      {@html formatTextTemplate(tooltipTextTemplate, {
        state: tooltip.props.state,
        variable_name: cleanVariableName(config.columns.get(metric)?.column_label),
        value: config.columns.get(metric)?.format(tooltip.props[metric])?.replace("%", " percent")
      })}
    </p>
  </Tooltip>
{/if}

<style>
  .clear-selection-wrapper {
    display: flex;
    justify-content: flex-end;
  }

  .clear-selection-wrapper button {
    appearance: none;
    border: none;
    background: none;
    color: inherit;
    border-bottom: solid 1px var(--color-gray-shade-darkest);
    font-style: italic;
    font-family: inherit;
    cursor: pointer;
    outline: none;
    margin-top: 0.5em;
  }
  .clear-selection-wrapper button:focus {
    outline: var(--color-yellow) solid 2px;
    outline-offset: var(--spacing-2);
  }

  .map-placeholder {
    width: 100%;
    padding-top: 62.5%;
    background-color: var(--color-gray-shade-lightest);
  }
</style>
